import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useCoMingleReturns } from '@/pages/return/coMingle'
import { getReturnReceiptItems, getReturnReceiptSummaries } from './apis'
import { toItem, toSummary } from './mappers'
import { ReturnReceiptFilter, ReturnReceiptItem, ReturnReceiptItemRes, ReturnReceiptSummary } from './types'

const PATH = ['return', 'returnReceipt'] as const

export const returnReceiptQueryKeys = {
  all: PATH,
  summaries: (filter: ReturnReceiptFilter | null) => [...PATH, 'SUMMARIES', filter] as const,
  items: (filter: ReturnReceiptFilter | null) => [...PATH, 'ITEMS', filter] as const,
}

type QueryStateOf<TData> = Omit<UseQueryResult<TData, Error>, 'data'>
type SummariesResult = QueryStateOf<ReturnReceiptSummary[]> & { items: ReturnReceiptSummary[] }
type ItemsResult = QueryStateOf<ReturnReceiptItem[]> & { items: ReturnReceiptItem[] }

/**
 * 머리표 목록.
 *
 * 진입 즉시 조회하지 않는다(`enabled: false`) — 센터도착일자가 필수라 조회 버튼으로만 실행한다.
 */
export const useGetReturnReceiptSummaries = (filter: ReturnReceiptFilter | null): SummariesResult => {
  const { data, ...rest } = useQuery({
    queryKey: returnReceiptQueryKeys.summaries(filter),
    queryFn: async () => {
      const res = await getReturnReceiptSummaries(filter as ReturnReceiptFilter)
      return (res.data?.items ?? []).map(toSummary)
    },
    enabled: false,
    meta: { noErrorMessage: true }, // 실패는 화면(search 슬롯)이 한 번 알린다 — 전역 알림까지 뜨면 알럿이 두 장 겹친다(2026-10-02 QA)
  })

  return { items: data ?? [], ...rest }
}

/** 상세표 목록 — 전건을 받아 탭 필터는 화면에서 한다. */
export const useGetReturnReceiptItems = (filter: ReturnReceiptFilter | null): ItemsResult => {
  // 플래그는 표시 변환(select)에만 쓰여 쿼리 키에 넣지 않는다. 값이 바뀌면 서버 재요청 없이 캐시 데이터에 select 가 다시 적용된다.
  const coMingleEnabled = useCoMingleReturns()
  const selectItems = useCallback((items: ReturnReceiptItemRes[]) => items.map((item) => toItem(item, coMingleEnabled)), [coMingleEnabled])
  const { data, ...rest } = useQuery({
    queryKey: returnReceiptQueryKeys.items(filter),
    queryFn: async () => {
      const res = await getReturnReceiptItems(filter as ReturnReceiptFilter)
      return res.data?.items ?? []
    },
    enabled: false,
    select: selectItems,
    meta: { noErrorMessage: true }, // 실패는 화면(RGR09 요약 행 선택 — onPick 배선)이 한 번 알린다 — 전역 알림까지 뜨면 알럿이 두 장 겹친다(2026-10-02 QA)
  })

  return { items: data ?? [], ...rest }
}
